import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { ArrowLeft, Plus, Scales, Timer, MapPin, House, ChartLineUp, Medal, ListBullets, Lightbulb } from '@phosphor-icons/react';

const Progress = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [weightLogs, setWeightLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [cardioLogs, setCardioLogs] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newWeight, setNewWeight] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const config = { withCredentials: true, headers };
        const [weightRes, statsRes, logsRes, insightsRes] = await Promise.all([
          axios.get(`${API}/weight-logs`, config),
          axios.get(`${API}/stats`, config),
          axios.get(`${API}/cardio-logs`, config),
          axios.get(`${API}/insights`, config)
        ]);
        setWeightLogs(weightRes.data);
        setStats(statsRes.data);
        setCardioLogs(logsRes.data);
        setInsights(insightsRes.data.insights || []);
      } catch (error) {
        console.error('Failed to fetch progress data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const handleAddWeight = async () => {
    if (!newWeight) return;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/weight-logs`, { weight: parseFloat(newWeight) }, { withCredentials: true, headers });
      setWeightLogs([response.data, ...weightLogs]);
      setNewWeight('');
      setDialogOpen(false);
      toast.success('Weight logged!');
    } catch (error) {
      toast.error('Failed to log weight');
    }
  };

  const weightChartData = [...weightLogs].reverse().slice(-14).map(log => ({
    date: new Date(log.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    weight: log.weight
  }));

  const weeklyMinutes = cardioLogs.reduce((acc, log) => {
    const weekStart = new Date(log.date);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const weekKey = weekStart.toISOString().split('T')[0];
    acc[weekKey] = (acc[weekKey] || 0) + log.duration_minutes;
    return acc;
  }, {});

  const minutesChartData = Object.entries(weeklyMinutes).slice(-8).map(([week, minutes]) => ({
    week: new Date(week).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    minutes
  }));

  if (loading) {
    return (<div className="min-h-screen bg-background flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>);
  }

  return (
    <div className="min-h-screen bg-background pb-24 hexagon-bg" data-testid="progress-screen">
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="w-10 h-10 rounded-full bg-card flex items-center justify-center" data-testid="back-btn"><ArrowLeft weight="bold" className="w-5 h-5 text-foreground" /></button>
          <div className="flex-1"><h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Progress</h1><p className="text-xs text-muted-foreground">Track your journey</p></div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-6 py-6 space-y-6">
        {/* Smart Insights */}
        {insights.length > 0 && (
          <div className="premium-card p-5" data-testid="smart-insights">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb weight="fill" className="w-4 h-4 text-[#D4AF37]" />
              <h3 className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold">Smart Insights</h3>
            </div>
            <div className="space-y-2">
              {insights.map((insight, idx) => (
                <div key={idx} className="flex items-start gap-2 py-1.5 border-b border-border/50 last:border-0">
                  <span className="text-[#D4AF37] text-xs mt-0.5">*</span>
                  <p className="text-sm text-muted-foreground" data-testid={`insight-${idx}`}>{insight.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats Summary */}
        <div className="grid grid-cols-3 gap-4">
          <div className="premium-card p-4 text-center">
            <Timer weight="duotone" className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-2xl font-black text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats?.total_minutes || 0}</p>
            <p className="text-xs text-muted-foreground">Total Minutes</p>
          </div>
          <div className="premium-card p-4 text-center">
            <MapPin weight="duotone" className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-2xl font-black text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats?.total_distance || 0}</p>
            <p className="text-xs text-muted-foreground">Total Miles</p>
          </div>
          <div className="premium-card p-4 text-center">
            <Scales weight="duotone" className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-2xl font-black text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{weightLogs[0]?.weight || '-'}</p>
            <p className="text-xs text-muted-foreground">Current lbs</p>
          </div>
        </div>

        {/* Weight Chart */}
        <div className="premium-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold">Weight Progress</h3>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[#D4AF37] text-black rounded-full h-8 px-4" data-testid="add-weight-btn"><Plus weight="bold" className="w-4 h-4 mr-1" />Add</Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
                <DialogHeader><DialogTitle className="text-foreground">Log Weight</DialogTitle></DialogHeader>
                <div className="space-y-4 py-4">
                  <div><Label className="text-muted-foreground">Weight (lbs)</Label><Input type="number" step="0.1" value={newWeight} onChange={(e) => setNewWeight(e.target.value)} placeholder="150" className="mt-2 bg-background" data-testid="weight-input" /></div>
                  <Button onClick={handleAddWeight} className="w-full bg-[#D4AF37] text-black rounded-full" data-testid="save-weight-btn">Save Weight</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          {weightChartData.length > 0 ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weightChartData}>
                  <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis domain={['dataMin - 5', 'dataMax + 5']} tick={{ fill: '#71717a', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#121212', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '8px' }} labelStyle={{ color: '#fff' }} />
                  <Line type="monotone" dataKey="weight" stroke="#D4AF37" strokeWidth={2} dot={{ fill: '#D4AF37', r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (<p className="text-center text-muted-foreground py-8">No weight data yet. Add your first entry!</p>)}
        </div>

        {/* Weekly Cardio Chart */}
        <div className="premium-card p-6">
          <h3 className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-4">Weekly Cardio Minutes</h3>
          {minutesChartData.length > 0 ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={minutesChartData}>
                  <XAxis dataKey="week" tick={{ fill: '#71717a', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#71717a', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#121212', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '8px' }} labelStyle={{ color: '#fff' }} />
                  <Line type="monotone" dataKey="minutes" stroke="#22C55E" strokeWidth={2} dot={{ fill: '#22C55E', r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (<p className="text-center text-muted-foreground py-8">Start logging cardio to see your progress!</p>)}
        </div>

        {/* Recent Weight Logs */}
        {weightLogs.length > 0 && (
          <div className="premium-card p-6">
            <h3 className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-4">Recent Weight Entries</h3>
            <div className="space-y-3">
              {weightLogs.slice(0, 5).map((log) => (
                <div key={log.log_id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <span className="text-muted-foreground text-sm">{new Date(log.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  <span className="text-foreground font-semibold">{log.weight} lbs</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <nav className="fixed bottom-0 left-0 right-0 bg-background/90 backdrop-blur-xl border-t border-border px-6 py-3 z-50">
        <div className="max-w-lg mx-auto flex items-center justify-around">
          <Link to="/dashboard" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-home"><House weight="duotone" className="w-6 h-6" /><span className="text-[10px]">Home</span></Link>
          <Link to="/history" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-history"><ListBullets weight="duotone" className="w-6 h-6" /><span className="text-[10px]">History</span></Link>
          <Link to="/progress" className="flex flex-col items-center gap-1 text-[#D4AF37]" data-testid="nav-progress"><ChartLineUp weight="fill" className="w-6 h-6" /><span className="text-[10px]">Progress</span></Link>
          <Link to="/milestones" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-milestones"><Medal weight="duotone" className="w-6 h-6" /><span className="text-[10px]">Badges</span></Link>
        </div>
      </nav>
    </div>
  );
};

export default Progress;
